import {
    IonButton,
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonCardTitle,
    IonContent,
    IonFooter,
    IonIcon,
    IonItem,
    IonLabel,
    IonList,
    IonPage,
    IonProgressBar,
    useIonRouter,
} from "@ionic/react";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { HeaderComponent } from "../../components";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { routes } from "../../route/constants";
import { setShowOnBoardingFlow } from "../../redux/actions/appStatus";
import { isIos } from "../../services/platformDetection";
import {
    CheckApplicationInfoResult,
    GarminSDK,
    IQDevice,
} from "capacitor-garmin-sdk";
import {
    setGarminKnownDevices,
    setGarminSelectedDevice,
} from "../../redux/actions/garminActions";
import {
    checkmarkCircle,
    logoAppleAppstore,
    logoGooglePlaystore,
    reload,
} from "ionicons/icons";
import {
    filterKnownDevices,
    isGarminBTCompatible,
    isGarminLiteDevice,
    openConnectMobile,
    openStoreForConnectMobile,
    updateGarminKnownDevices,
    updateSelectedDevice,
} from "../../services/watch/GarminWatchHelper";
import { GARMIN_FULL_APP_INFO } from "../../constants/GarminConstants";

export const OnboardingSelectGarmin: FC = () => {
    const { t } = useTranslation();
    const route = useIonRouter();

    const knownDevices = useSelector(
        (state: RootState) => state.garminWatches?.knownDevices
    );
    const selectedDevice = useSelector(
        (state: RootState) => state.garminWatches?.selectedDevice
    );
    const developerToolsEnabled = useSelector(
        (state: RootState) => state.developerTools.enabled
    );
    const [appInfo, setAppInfo] = useState<
        CheckApplicationInfoResult | undefined
    >(undefined);
    const [filteredKnownDevices, setFilteredKnownDevices] = useState<
        IQDevice[]
    >([]);

    const dispatch = useDispatch();

    const init = async () => {
        // check that the Garmin Connect app is installed on the phone
        const { installed } = await GarminSDK.checkGarminConnectInstalled();
        if (!installed) {
            // if not installed on iOS, open the app store explicitly.
            // android implementation with do this when we call getKnownDevices
            openStoreForConnectMobile();
        }
        try {
            await updateGarminKnownDevices();
        } catch (e) {
            console.error("Error updating known devices", e);
        }
    };

    const handleWatchTapped = async (device: IQDevice) => {
        if (isGarminLiteDevice(device.friendlyName)) {
            // check if app installed
            return goToWebPairing(device, true);
        } else {
            // Bluetooth pairing
            return await handleGarminWatchTapped(device);
        }
    };

    const handleGarminWatchTapped = async (device: IQDevice) => {
        await updateSelectedDevice(device);
        return route.push(routes.onboardingFoundWatch + "/garmin");
    };

    const goToWebPairing = (device: IQDevice, isLite?: boolean) => {
        const slug = isLite ? "garmin-lite" : "garmin";
        return route.push(
            routes.onboardingPairWatch + `/${slug}/${device.uuid}`
        );
    };

    const isSelectedDevice = (device: IQDevice) => {
        return selectedDevice?.uuid === device.uuid;
    };

    const selectedDeviceNeedsUpdate = () => {
        return (
            selectedDevice &&
            appInfo &&
            typeof appInfo.version === "number" &&
            !isGarminLiteDevice(selectedDevice.friendlyName) &&
            !isGarminBTCompatible(appInfo?.version)
        );
    };

    useEffect(() => {
        if (!knownDevices) {
            init();
        }
        console.log("knownDevices", knownDevices);
    }, [knownDevices]);

    useEffect(() => {
        if (knownDevices) {
            setFilteredKnownDevices(filterKnownDevices(knownDevices));
        }
    }, [knownDevices]);

    return (
        <IonPage>
            <HeaderComponent
                titleTestId="select-watch-header"
                title={t("help.onBoarding.step0a")}
                showProgressBar={true}
                progressValue={0.7}
            />
            <IonContent>
                <IonCard
                    style={{
                        "--background": "transparent",
                    }}
                >
                    <IonItem lines="none" color="transparet">
                        <IonLabel className="text-weight-bold" color="primary">
                            {t("settings.WatchSettings.selectWatchTitle")}:
                        </IonLabel>
                        <IonButton
                            fill="clear"
                            onClick={() => {
                                init();
                            }}
                        >
                            <IonIcon icon={reload} slot="end" />
                        </IonButton>
                    </IonItem>
                </IonCard>

                <IonCard>
                    <IonList>
                        {filteredKnownDevices.map((device, index) => {
                            return (
                                <IonItem
                                    key={device.uuid || index}
                                    onClick={() => handleWatchTapped(device)}
                                >
                                    {isSelectedDevice(device) && (
                                        <IonIcon
                                            icon={checkmarkCircle}
                                            slot="end"
                                            color="primary"
                                        />
                                    )}
                                    <IonLabel>
                                        <h2
                                            style={{
                                                fontSize: 20,
                                            }}
                                        >
                                            {device.friendlyName ||
                                                device.modelName}
                                        </h2>
                                        <p>
                                            {isGarminLiteDevice(
                                                device.friendlyName
                                            ) && <span> REFSIX Lite App</span>}

                                            {isSelectedDevice(device) &&
                                                selectedDeviceNeedsUpdate() && (
                                                    <span>
                                                        {t(
                                                            "general.updateRequired"
                                                        )}
                                                    </span>
                                                )}
                                        </p>
                                    </IonLabel>
                                </IonItem>
                            );
                        })}
                    </IonList>

                    {filteredKnownDevices.length === 0 && (
                        <>
                            <IonCardHeader>
                                <IonCardTitle>
                                    {t("help.onBoarding.noWatchFound")}
                                </IonCardTitle>
                            </IonCardHeader>
                            <IonCardContent>
                                <p>{t("garminWatches.watchNotConnected")}</p>
                                <IonButton
                                    onClick={async () => {
                                        openConnectMobile().catch(() => {});
                                    }}
                                >
                                    {t("general.openApp", {
                                        appName: "Garmin Connect",
                                    })}
                                </IonButton>
                            </IonCardContent>
                        </>
                    )}
                </IonCard>

                {developerToolsEnabled && (
                    <IonCard>
                        <IonCardHeader>
                            <IonCardTitle>Developer Tools</IonCardTitle>
                        </IonCardHeader>

                        <IonCardContent>
                            <IonButton
                                size="small"
                                onClick={() => {
                                    dispatch(
                                        setGarminSelectedDevice(undefined)
                                    );
                                }}
                            >
                                Reset Selected Device
                            </IonButton>

                            <IonButton
                                size="small"
                                onClick={() => {
                                    init();
                                }}
                            >
                                RefreshDevices
                            </IonButton>

                            <IonButton
                                size="small"
                                disabled={!selectedDevice}
                                onClick={() => {
                                    if (!selectedDevice) {
                                        return;
                                    }
                                    updateSelectedDevice(selectedDevice);
                                }}
                            >
                                Refresh Selected Device
                            </IonButton>

                            <IonButton
                                size="small"
                                onClick={() => {
                                    route.push(routes.garminDebugger);
                                }}
                            >
                                Garmin Debugger
                            </IonButton>

                            <IonButton
                                size="small"
                                onClick={async () => {
                                    if (!selectedDevice) {
                                        return;
                                    }
                                    try {
                                        await GarminSDK.showConnectIQStoreForApp(
                                            {
                                                device: selectedDevice,
                                                app: GARMIN_FULL_APP_INFO,
                                            }
                                        );
                                    } catch (e) {
                                        console.error(`Error showing store`, e);
                                    }
                                }}
                            >
                                Test Plugin Open Store
                            </IonButton>

                            <h2>Selected Device</h2>
                            <pre>
                                {JSON.stringify(
                                    selectedDevice,

                                    null,
                                    2
                                )}
                            </pre>

                            {selectedDevice && appInfo && (
                                <>
                                    <h2>App Info</h2>
                                    <pre>
                                        {JSON.stringify(
                                            appInfo,

                                            null,
                                            2
                                        )}
                                    </pre>
                                </>
                            )}

                            <h2>Known Devices</h2>
                            <pre>
                                {JSON.stringify(
                                    knownDevices,

                                    null,
                                    2
                                )}
                            </pre>
                        </IonCardContent>
                    </IonCard>
                )}
            </IonContent>
            <IonFooter>
                <IonButton
                    fill={"clear"}
                    expand={"full"}
                    onClick={async () => {
                        await dispatch(setShowOnBoardingFlow(false));
                        route.push(routes.tabResults, "root", "replace");
                    }}
                >
                    {t("general.skip")}
                </IonButton>
            </IonFooter>
        </IonPage>
    );
};
